// src/products/equityManager/state/equityManagerStore.js
import { create } from 'zustand';
import RoleService from '../../../services/roleService';
import EquityService from '../../../services/equityService';
import { useGlobalStore } from '../../../state/globalStore';
import activityLogService from '../../../services/activityLogService';

export const useEquityManagerStore = create((set, get) => ({
  // --- STATE ---
  companies: [],
  selectedCompany: null,
  shareholders: [],
  directors: [],
  shareClasses: [],
  issuances: [],
  userProfile: null,
  loading: true,
  loadingCompanyData: false,
  messages: [],
  userRole: null, // Company-specific role
  canCreate: false, // Product-level permission

  // --- ACTIONS ---
  addMessage: (content, type = 'info') => {
    const id = Date.now();
    set(state => ({ messages: [...state.messages, { id, content, type }] }));
    setTimeout(() => {
      set(state => ({ messages: state.messages.filter(msg => msg.id !== id) }));
    }, 5000);
  },

  addSuccess: (msg) => get().addMessage(msg, 'success'),
  addError: (msg) => get().addMessage(msg, 'error'),

  checkCanCreate: async () => {
    const user = useGlobalStore.getState().user;
    if (!user?.id) return;
    try {
      const canCreateCompanies = await RoleService.canCreateInProduct(user.id, RoleService.PRODUCTS.EQUITY_MANAGER);
      set({ canCreate: canCreateCompanies });
    } catch (error) {
      console.error('Error checking create permission:', error);
      set({ canCreate: false });
    }
  },

  checkCompanyRole: async (companyId) => {
    const user = useGlobalStore.getState().user;
    if (!user?.id || !companyId) {
      set({ userRole: null });
      return;
    }
    try {
      const role = await RoleService.getEquityRole(user.id, companyId);
      set({ userRole: role || 'viewer' });
    } catch (error) {
      console.error('Error checking company role:', error);
      set({ userRole: 'viewer' });
    }
  },

  loadInitialData: async () => {
    const user = useGlobalStore.getState().user;
    if (!user?.id) return;
    try {
      set({ loading: true });
      await get().checkCanCreate();

      const profile = await EquityService.fetchUserProfile(user.id);
      set({ userProfile: profile });

      const companiesData = await EquityService.fetchCompanies(user.id);
      set({ companies: companiesData });

      if (companiesData.length > 0 && !get().selectedCompany) {
        set({ selectedCompany: companiesData[0] });
      }
    } catch (error) {
      console.error('Error loading initial user data:', error);
      get().addError('Failed to load user data');
    } finally {
      set({ loading: false });
    }
  },

  loadCompanyData: async () => {
    const { selectedCompany } = get();
    if (!selectedCompany?.id) return;
    try {
      set({ loadingCompanyData: true });
      const [shareholdersData, directorsData, shareClassesData, issuancesData] = await Promise.all([
        EquityService.fetchShareholders(selectedCompany.id),
        EquityService.fetchDirectors(selectedCompany.id),
        EquityService.fetchShareClasses(selectedCompany.id),
        EquityService.fetchIssuances(selectedCompany.id)
      ]);
      set({
        shareholders: shareholdersData || [],
        directors: directorsData || [],
        shareClasses: shareClassesData || [],
        issuances: issuancesData || []
      });
    } catch (error) {
      console.error('Error loading company data:', error);
      console.error('Full error details:', JSON.stringify(error, null, 2));
      get().addError('Failed to load company data');
    } finally {
      set({ loadingCompanyData: false });
    }
  },

  handleCompanyChange: (company) => {
    set({ selectedCompany: company });
    
    // Log company access
    if (company?.id) {
      activityLogService.logCompanyAccess(company.id, company.name).catch(err =>
        console.warn('Failed to log company access:', err)
      );
    }
  },

  handleCompanyCreated: async (newCompany) => {
    const user = useGlobalStore.getState().user;
    if (!get().canCreate || !user?.id) {
      get().addError('You do not have permission to create companies.');
      return;
    }
    try {
      await RoleService.assignEquityRole(user.id, newCompany.id, RoleService.ROLES.CREATOR);
      const companiesData = await EquityService.fetchCompanies(user.id);
      set({ companies: companiesData, selectedCompany: newCompany });
      get().addSuccess('Company created successfully');
    } catch (error) {
      console.error('Error setting up company roles:', error);
      get().addError('Company created but role assignment failed');
    }
  },

  refreshData: async () => {
    const user = useGlobalStore.getState().user;
    if (!user?.id) return;
    const { selectedCompany } = get();
    const companiesData = await EquityService.fetchCompanies(user.id);
    set({ companies: companiesData });
    if (selectedCompany) {
      await get().loadCompanyData();
    }
  },
}));
